import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { PageContainer } from '@/components/layout'
import { Card, Button, Input, Spinner, Modal, Badge, ToastContainer, useToast } from '@/components/ui'
import { api, ApiError } from '@/services/api'
import { getErrorMessage } from '@/services/errors'
import { useAccounts } from '@/context/AccountContext'
import { AccountAvatar } from '@/components/AccountAvatar'
import { AccountVoiceConfig } from '@/components/AccountVoiceConfig'
import { PLATFORM_CONFIGS, getPlatformDisplayName } from '@/services/platforms'
import {
  IDLE_CONNECTION_ATTEMPT,
  canRemoveAccountPermanently,
  connectionAttemptReducer,
  partitionManagedAccounts,
  reconcileManagedAccountsFallback,
  removeManagedAccountLocally,
} from '@/services/accounts'
import { ErrorCode } from '@/services/types'
import type { AccountPlatform, ManagedAccount } from '@/services/types'

const CONNECT_PLATFORM_ORDER: AccountPlatform[] = [
  'twitter',
  'bluesky',
  'threads',
  'truthsocial',
  'linkedin',
  'facebook',
]

export function AccountsPage() {
  const { toasts, addToast, dismissToast } = useToast()
  const { accounts, isLoading: accountsLoading, refreshAccounts } = useAccounts()
  const [managedAccounts, setManagedAccounts] = useState<ManagedAccount[]>([])
  const [managedAccountsLoading, setManagedAccountsLoading] = useState(true)
  const [managedAccountsError, setManagedAccountsError] = useState(false)
  const [connectionAttempt, dispatchConnectionAttempt] = useReducer(
    connectionAttemptReducer,
    IDLE_CONNECTION_ATTEMPT
  )
  const attemptIdRef = useRef(0)
  const [disconnectingId, setDisconnectingId] = useState<number | null>(null)
  const [removingId, setRemovingId] = useState<number | null>(null)
  const [showConnectModal, setShowConnectModal] = useState<AccountPlatform | null>(null)
  const [showDisconnectModal, setShowDisconnectModal] = useState<number | null>(null)
  const [showRemoveModal, setShowRemoveModal] = useState<number | null>(null)
  const [connectHandle, setConnectHandle] = useState('')

  const refreshManagedAccounts = useCallback(async () => {
    setManagedAccountsLoading(true)
    try {
      const response = await api.getManagedAccounts()
      setManagedAccounts(response.accounts)
      setManagedAccountsError(false)
    } catch (err) {
      setManagedAccountsError(true)
      setManagedAccounts((current) => reconcileManagedAccountsFallback(current, accounts))
      addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to load account status') : 'Failed to load account status', 'error')
    } finally {
      setManagedAccountsLoading(false)
    }
  }, [accounts, addToast])

  useEffect(() => {
    refreshManagedAccounts()
  }, [refreshManagedAccounts])

  const { connected, reconnectRequired } = partitionManagedAccounts(managedAccounts)

  // An abandoned OAuth redirect can bring the page back from the BFCache with
  // stale pending state; pageshow fires on both normal loads and BFCache
  // restores, so reset any in-flight attempt whenever it fires.
  useEffect(() => {
    const handlePageShow = () => {
      attemptIdRef.current += 1
      dispatchConnectionAttempt({ type: 'page-show' })
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  const cancelConnectionAttempt = () => {
    attemptIdRef.current += 1
    dispatchConnectionAttempt({ type: 'cancel' })
  }

  const closeConnectModal = () => {
    setShowConnectModal(null)
    setConnectHandle('')
    cancelConnectionAttempt()
  }

  const handleConnect = async (platform: AccountPlatform) => {
    const config = PLATFORM_CONFIGS[platform]
    const handle = connectHandle.trim()
    setShowConnectModal(null)
    setConnectHandle('')
    const attemptId = ++attemptIdRef.current
    dispatchConnectionAttempt({ type: 'start-connect', platform })
    try {
      const authUrl = await api.startPlatformOAuth(platform, config.requiresHandle && handle ? { handle } : undefined)
      if (attemptIdRef.current !== attemptId) return
      dispatchConnectionAttempt({ type: 'auth-navigation' })
      window.location.assign(authUrl)
    } catch (err) {
      if (attemptIdRef.current !== attemptId) return
      dispatchConnectionAttempt({ type: 'failed' })
      const name = getPlatformDisplayName(platform)
      addToast(err instanceof ApiError ? getErrorMessage(err, `Failed to connect ${name}`) : `Failed to connect ${name}`, 'error')
    }
  }

  const handleDisconnect = async (accountId: number) => {
    const account = managedAccounts.find((item) => item.id === accountId)
    if (!account) return

    setShowDisconnectModal(null)
    setDisconnectingId(account.id)
    try {
      await api.disconnectAccount(account.id)
      setManagedAccounts((current) => current.map((item) => item.id === account.id
        ? {
            ...item,
            connection_status: 'reconnect_required',
            disconnected_at: new Date().toISOString(),
          }
        : item))
      await refreshAccounts()
      addToast(`@${account.username} disconnected. Saved Beacon data was retained.`, 'success')
    } catch (err) {
      addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to disconnect account') : 'Failed to disconnect account', 'error')
    } finally {
      setDisconnectingId(null)
    }
  }

  const handleReconnect = async (account: ManagedAccount) => {
    const attemptId = ++attemptIdRef.current
    dispatchConnectionAttempt({ type: 'start-reconnect', accountId: account.id })
    try {
      const authUrl = await api.reconnectAccount(account.id)
      if (attemptIdRef.current !== attemptId) return
      dispatchConnectionAttempt({ type: 'auth-navigation' })
      window.location.assign(authUrl)
    } catch (err) {
      if (attemptIdRef.current !== attemptId) return
      dispatchConnectionAttempt({ type: 'failed' })
      addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to reconnect account') : 'Failed to reconnect account', 'error')
    }
  }

  const handleRemovePermanently = async (accountId: number) => {
    const account = managedAccounts.find((item) => item.id === accountId)
    if (!account || !canRemoveAccountPermanently(account)) return

    setShowRemoveModal(null)
    setRemovingId(accountId)
    try {
      const response = await api.deleteAccountPermanently(accountId)
      setManagedAccounts((current) => removeManagedAccountLocally(current, accountId))
      await Promise.all([refreshAccounts(), refreshManagedAccounts()])
      addToast(response.message || `@${account.username} was removed from Beacon.`, 'success')
    } catch (err) {
      if (err instanceof ApiError && err.code === ErrorCode.ACCOUNT_NOT_FOUND) {
        // Already gone server-side; drop the stale card and resync.
        setManagedAccounts((current) => removeManagedAccountLocally(current, accountId))
        await Promise.all([refreshAccounts(), refreshManagedAccounts()])
      }
      addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to remove account from Beacon') : 'Failed to remove account from Beacon', 'error')
    } finally {
      setRemovingId(null)
    }
  }

  const handleVoiceConfigUpdate = async () => {
    await Promise.all([refreshAccounts(), refreshManagedAccounts()])
  }

  const connectModalPlatform = showConnectModal ? PLATFORM_CONFIGS[showConnectModal] : null
  const removeModalAccount = showRemoveModal !== null
    ? managedAccounts.find((item) => item.id === showRemoveModal) ?? null
    : null

  if (accountsLoading || managedAccountsLoading) {
    return (
      <PageContainer title="Accounts">
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer title="Accounts">
      <div className="space-y-6">
        {managedAccountsError && (
          <div className="border border-error/30 bg-error/5 p-4 sm:flex sm:items-center sm:justify-between">
            <p className="text-sm text-error">
              Account connection status could not be refreshed. Connected accounts remain available below.
            </p>
            <Button variant="secondary" onClick={refreshManagedAccounts} className="mt-3 sm:mt-0">
              Retry
            </Button>
          </div>
        )}

        {reconnectRequired.length > 0 && (
          <div className="border border-amber-300 bg-amber-50 p-4">
            <p className="font-medium text-amber-900">
              {reconnectRequired.length} {reconnectRequired.length === 1 ? 'account needs' : 'accounts need'} to be reconnected.
            </p>
            <p className="text-sm text-amber-800 mt-1">
              Your saved content and voice settings have not been deleted.
            </p>
          </div>
        )}

        {/* Connected accounts */}
        {connected.map((account) => (
          <Card key={account.id}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <AccountAvatar account={account} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium">{account.display_name || `@${account.username}`}</p>
                  <Badge variant="outline">{getPlatformDisplayName(account.platform)}</Badge>
                </div>
                <p className="text-sm text-secondary-text">@{account.username}</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-success rounded-full" />
                  <span className="text-sm text-secondary-text">Connected</span>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => setShowDisconnectModal(account.id)}
                  loading={disconnectingId === account.id}
                  className="text-error hover:text-error"
                >
                  Disconnect
                </Button>
              </div>
            </div>
            <p className="text-xs text-secondary-text mt-2">
              Connected since {new Date(account.connected_at).toLocaleDateString()}
            </p>

            <AccountVoiceConfig
              account={account}
              onUpdate={handleVoiceConfigUpdate}
              addToast={addToast}
            />
          </Card>
        ))}

        {/* Retained accounts that need OAuth reconnection */}
        {reconnectRequired.map((account) => (
          <Card key={account.id}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <AccountAvatar account={account} dimmed />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium">{account.display_name || `@${account.username}`}</p>
                  <Badge variant="outline">{getPlatformDisplayName(account.platform)}</Badge>
                </div>
                <p className="text-sm text-secondary-text">@{account.username}</p>
              </div>
              <div className="flex flex-col gap-3 sm:items-end">
                <div className="flex items-center gap-2 text-amber-700">
                  <span className="w-2 h-2 bg-amber-500 rounded-full" />
                  <span className="text-sm">Reconnect required</span>
                </div>
                {connectionAttempt.status === 'pending' &&
                connectionAttempt.kind === 'reconnect' &&
                connectionAttempt.accountId === account.id ? (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <span className="text-sm text-secondary-text" role="status">
                      Continuing to {getPlatformDisplayName(account.platform)} in your browser&hellip;
                    </span>
                    <Button variant="secondary" onClick={cancelConnectionAttempt}>
                      Cancel connection attempt
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Button
                      variant="secondary"
                      onClick={() => setShowRemoveModal(account.id)}
                      loading={removingId === account.id}
                      className="text-error hover:text-error"
                    >
                      Remove from Beacon
                    </Button>
                    <Button
                      onClick={() => handleReconnect(account)}
                      disabled={!account.reconnect_supported || removingId === account.id}
                    >
                      {account.reconnect_supported ? `Reconnect @${account.username}` : 'Reconnect unavailable'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <p className="text-sm text-secondary-text mt-4">
              {account.has_retained_voice_profile
                ? 'Beacon still has your voice profile and saved Beacon data.'
                : 'Beacon still retains this account and its saved Beacon data.'}{' '}
              Reconnect to import posts, generate from current {getPlatformDisplayName(account.platform)} data,
              or publish.
            </p>
            <p className="text-sm text-secondary-text mt-2">
              Reconnecting continues to {getPlatformDisplayName(account.platform)} in your browser — stay in the
              browser to finish. If you renamed your handle, you can still reconnect: Beacon matches the same
              underlying {getPlatformDisplayName(account.platform)} account.
            </p>
            {account.disconnected_at && (
              <p className="text-xs text-secondary-text mt-2">
                Disconnected {new Date(account.disconnected_at).toLocaleDateString()}
              </p>
            )}
          </Card>
        ))}

        {/* Platform connection cards */}
        {CONNECT_PLATFORM_ORDER.map((platformId) => {
          const config = PLATFORM_CONFIGS[platformId]
          const hasAccount = managedAccounts.some((a) => a.platform === platformId)

          if (hasAccount && !config.enabled) return null

          const isConnectPending = connectionAttempt.status === 'pending' &&
            connectionAttempt.kind === 'connect' &&
            connectionAttempt.platform === platformId

          return (
            <Card key={platformId}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="font-medium">{config.displayName}</h3>
                {config.enabled ? (
                  isConnectPending ? (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <span className="text-sm text-secondary-text" role="status">
                        Continuing to {config.displayName} in your browser&hellip;
                      </span>
                      <Button variant="secondary" onClick={cancelConnectionAttempt}>
                        Cancel connection attempt
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant={hasAccount ? 'secondary' : 'primary'}
                      onClick={() => setShowConnectModal(platformId)}
                    >
                      {hasAccount ? `Connect Another ${config.displayName}` : `Connect ${config.displayName}`}
                    </Button>
                  )
                ) : (
                  <Button variant="secondary" disabled>
                    Coming Soon
                  </Button>
                )}
              </div>
              {!config.enabled && (
                <p className="text-sm text-secondary-text mt-2">Coming soon</p>
              )}
              {config.enabled && !hasAccount && (
                <p className="text-secondary-text text-sm mt-2">
                  Connect your {config.displayName} account to post content and analyze your voice.
                </p>
              )}
            </Card>
          )
        })}
      </div>

      {/* Connect Modal */}
      <Modal
        isOpen={showConnectModal !== null}
        onClose={closeConnectModal}
        title={connectModalPlatform ? `Connect ${connectModalPlatform.displayName} Account` : 'Connect Account'}
        actions={
          <>
            <Button variant="secondary" onClick={closeConnectModal}>
              Cancel
            </Button>
            <Button
              onClick={() => showConnectModal && handleConnect(showConnectModal)}
              disabled={connectModalPlatform?.requiresHandle && !connectHandle.trim()}
            >
              Continue to {connectModalPlatform?.displayName} in browser
            </Button>
          </>
        }
      >
        {connectModalPlatform?.requiresHandle && (
          <div className="mb-4">
            <Input
              label={`${connectModalPlatform.displayName} Handle`}
              placeholder="username.bsky.social"
              value={connectHandle}
              onChange={(e) => setConnectHandle(e.target.value)}
            />
            <p className="text-xs text-secondary-text mt-1">
              Enter your {connectModalPlatform.displayName} handle so we can connect to your account.
            </p>
          </div>
        )}
        <p className="text-secondary-text mb-4">
          You'll continue to {connectModalPlatform?.displayName} in your browser to authorize Beacon. We'll request the following permissions:
        </p>
        <ul className="space-y-3 text-sm">
          {connectModalPlatform?.connectPermissions.map((perm) => (
            <li key={perm} className="flex gap-3">
              <span className="text-success">&#10003;</span>
              <p>{perm}</p>
            </li>
          ))}
        </ul>
        {showConnectModal === 'twitter' && (
          <div className="mt-4 p-3 bg-secondary-bg rounded text-sm">
            <p className="text-secondary-text">
              <span className="font-medium">On mobile:</span> Authorization happens on X's website, in your browser.
              Stay in the browser to complete the connection — don't switch to the X app, and ignore any
              "Open in app" prompt.
            </p>
          </div>
        )}
        <p className="text-xs text-secondary-text mt-4">
          You can disconnect at any time from this page.
        </p>
      </Modal>

      {/* Disconnect Modal */}
      <Modal
        isOpen={showDisconnectModal !== null}
        onClose={() => setShowDisconnectModal(null)}
        title="Disconnect Account"
        actions={
          <>
            <Button variant="secondary" onClick={() => setShowDisconnectModal(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => showDisconnectModal !== null && handleDisconnect(showDisconnectModal)}
              className="bg-error hover:bg-error/90"
            >
              Disconnect
            </Button>
          </>
        }
      >
        <p className="text-secondary-text mb-4">
          Are you sure you want to disconnect this account?
        </p>
        <p className="text-sm text-secondary-text">
          You won't be able to post content or use voice matching for this account until you reconnect.
        </p>
      </Modal>

      {/* Remove from Beacon Modal */}
      <Modal
        isOpen={removeModalAccount !== null}
        onClose={() => setShowRemoveModal(null)}
        title="Remove Account from Beacon"
        actions={
          <>
            <Button variant="secondary" onClick={() => setShowRemoveModal(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => showRemoveModal !== null && handleRemovePermanently(showRemoveModal)}
            >
              Remove from Beacon
            </Button>
          </>
        }
      >
        <p className="text-secondary-text mb-4">
          Removing {removeModalAccount ? `@${removeModalAccount.username}` : 'this account'} from Beacon
          permanently deletes:
        </p>
        <ul className="space-y-2 text-sm mb-4">
          <li className="flex gap-3">
            <span className="text-error">&#10005;</span>
            <p>The account association with Beacon</p>
          </li>
          <li className="flex gap-3">
            <span className="text-error">&#10005;</span>
            <p>Its voice profile and imported samples</p>
          </li>
          <li className="flex gap-3">
            <span className="text-error">&#10005;</span>
            <p>Its generation-prompt history</p>
          </li>
        </ul>
        <p className="text-sm text-secondary-text mb-4">
          Existing saved drafts and content queue history are retained, but without the account association.
        </p>
        <p className="text-sm font-medium text-error">This action cannot be undone.</p>
      </Modal>

      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </PageContainer>
  )
}
